import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle, Download, Package } from "lucide-react";
import type { OrderWithItems } from "@shared/schema";

interface DeliveryNoteData {
  order: OrderWithItems;
  settings: Record<string, string>;
}

function getUnitLabel(unit: string): string {
  switch (unit) {
    case "pcs": return "st";
    case "bag": return "påse";
    case "box": return "låda";
    default: return unit;
  }
}

function formatDate(d: Date | string | null | undefined): string {
  if (!d) return "";
  const date = new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function PublicDeliveryNotePage() {
  const params = useParams<{ token: string }>();
  const token = params.token;

  const { data, isLoading, isError, error } = useQuery<DeliveryNoteData>({
    queryKey: ["/api/delivery-note", token],
    queryFn: async () => {
      const res = await fetch(`/api/delivery-note/${token}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-lg w-full space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-sm w-full text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <h1 className="text-xl font-semibold text-gray-900">Länken är ogiltig</h1>
          <p className="text-gray-500 text-sm">
            {(error as Error)?.message || "Följesedeln kunde inte hittas. Länken kan ha löpt ut."}
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { order, settings } = data;
  const companyName = settings.companyName || "Rätt Order";

  const totalQty = (order.items || []).reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-amber-700 to-amber-600 px-6 py-5 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Följesedel</h1>
              <p className="text-amber-100 text-sm mt-0.5">Order #{order.orderNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-white font-semibold">{companyName}</p>
              {settings.companyAddress && (
                <p className="text-amber-100 text-xs mt-0.5">{settings.companyAddress}</p>
              )}
            </div>
          </div>

          <div className="px-6 py-4 grid grid-cols-2 gap-6 border-b">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Avsändare</p>
              <p className="font-medium text-gray-800">{companyName}</p>
              {settings.companyAddress && <p className="text-sm text-gray-500">{settings.companyAddress}</p>}
              {settings.companyPhone && <p className="text-sm text-gray-500">{settings.companyPhone}</p>}
              {settings.companyEmail && <p className="text-sm text-gray-500">{settings.companyEmail}</p>}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Mottagare</p>
              <p className="font-medium text-gray-800">{order.client?.name}</p>
              {order.client?.address && <p className="text-sm text-gray-500">{order.client.address}</p>}
              {order.client?.phone && <p className="text-sm text-gray-500">{order.client.phone}</p>}
              {order.client?.email && <p className="text-sm text-gray-500">{order.client.email}</p>}
            </div>
          </div>

          <div className="px-6 py-4 border-b">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Leveransdatum</p>
              <p className="text-sm text-gray-700 font-medium">{formatDate(order.orderDate)}</p>
            </div>
          </div>

          <div className="px-6 py-4">
            <table className="w-full text-sm" data-testid="delivery-note-items">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Produkt</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Antal</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Enhet</th>
                </tr>
              </thead>
              <tbody>
                {(order.items || []).map((item, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2.5 text-gray-800 font-medium">{item.product?.name}</td>
                    <td className="py-2.5 text-right text-gray-700" data-testid={`qty-item-${i}`}>{item.quantity}</td>
                    <td className="py-2.5 text-right text-gray-500">{getUnitLabel(item.product?.sellUnit || "pcs")}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="pt-3 font-semibold text-gray-800">Totalt</td>
                  <td className="pt-3 text-right font-semibold text-gray-800" data-testid="total-qty">{totalQty}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {(order.client?.hasRut && order.client?.rutDetails) || order.client?.notes ? (
            <div className="px-6 py-4 bg-gray-50 grid grid-cols-2 gap-4 border-t">
              {order.client?.hasRut && order.client?.rutDetails && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Leveransrutt</p>
                  <p className="text-sm text-gray-700">{order.client.rutDetails}</p>
                </div>
              )}
              {order.client?.notes && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Anteckningar</p>
                  <p className="text-sm text-gray-700">{order.client.notes}</p>
                </div>
              )}
            </div>
          ) : null}

          <div className="px-6 py-4 border-t flex justify-center">
            <Button
              size="lg"
              className="w-full max-w-xs text-base font-semibold"
              onClick={() => window.open(`/api/delivery-note/${token}/pdf`, "_blank")}
              data-testid="button-download-pdf"
            >
              <Download className="h-5 w-5 mr-2" />
              Ladda ner PDF
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Genererat av {companyName} · Rätt Order
        </p>
      </div>
    </div>
  );
}
